import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import InsertChartRoundedIcon from '@mui/icons-material/InsertChartRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { FC, useMemo } from 'react';
import {
  formatYearRangeSubtitle,
  type YearsRevenueMode,
} from '../Reports/indianFinancialYear';
import RevenueCard, { RevenueMetric } from './RevenueCard';

export interface RevenueOverviewResponse {
  daily: RevenueMetric;
  mtd: RevenueMetric;
  ytd: RevenueMetric;
}

interface RevenueOverviewProps {
  data?: RevenueOverviewResponse;
  isLoading?: boolean;
  /** Defaults to "Revenue Overview" */
  sectionTitle?: string;
  /** Defaults to "Revenue uses confirmed bookings." */
  caption?: string;
  /** Years Revenue window: Indian FY (default) or calendar year. */
  yearMode?: YearsRevenueMode;
  onYearModeChange?: (mode: YearsRevenueMode) => void;
}

const COMPARE_LABELS = {
  daily: 'vs Day Before',
  mtd: 'vs Last Month',
  ytdFiscal: 'vs Prior FY',
  ytdCalendar: 'vs Prior Year',
} as const;

/** Map API fields and ensure compare label + % change are always present for cards. */
function normalizeRevenueMetric(
  metric: RevenueMetric & Record<string, unknown>,
  fallbackCompare: string,
): RevenueMetric {
  const rawCompare =
    metric.compareText ??
    metric.compare_text ??
    metric.comparisonText ??
    metric.comparison_text;
  const compareText = String(rawCompare ?? '').trim() || fallbackCompare;
  const rawPct = metric.percentageChange ?? metric.percentage_change;
  const percentageChange = Number(rawPct);
  const rawTitleContext = metric.titleContext ?? metric.title_context;
  const titleContext =
    typeof rawTitleContext === 'string' && rawTitleContext.trim() ? rawTitleContext.trim() : undefined;
  const rawActualized = metric.actualizedRevenue ?? metric.actualized_revenue;
  const actualizedNum = Number(rawActualized);
  const actualizedRevenue = Number.isFinite(actualizedNum) ? actualizedNum : undefined;
  return {
    ...metric,
    compareText,
    percentageChange: Number.isFinite(percentageChange) ? percentageChange : 0,
    titleContext,
    actualizedRevenue,
  };
}

const RevenueOverview: FC<RevenueOverviewProps> = ({
  data,
  sectionTitle = 'Revenue Overview',
  caption = 'Revenue uses confirmed bookings.',
  yearMode = 'FISCAL',
  onYearModeChange,
}) => {
  const resolved = useMemo(() => {
    if (!data) return null;
    const todaySubtitle = dayjs().format('ddd, D MMM YYYY');
    const ytdCompare = yearMode === 'CALENDAR' ? COMPARE_LABELS.ytdCalendar : COMPARE_LABELS.ytdFiscal;
    const ytd = normalizeRevenueMetric(data.ytd as RevenueMetric & Record<string, unknown>, ytdCompare);
    return {
      ...data,
      daily: {
        ...normalizeRevenueMetric(data.daily as RevenueMetric & Record<string, unknown>, COMPARE_LABELS.daily),
        titleContext: data.daily.titleContext?.trim() || todaySubtitle,
      },
      mtd: normalizeRevenueMetric(data.mtd as RevenueMetric & Record<string, unknown>, COMPARE_LABELS.mtd),
      ytd: {
        ...ytd,
        compareText: ytdCompare,
        titleContext: ytd.titleContext || formatYearRangeSubtitle(yearMode),
      },
    };
  }, [data, yearMode]);

  return (
    <Box sx={{ mb: 2.25 }}>
      <Box sx={{ mb: 1.25 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 800 }}>{sectionTitle}</Typography>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: 'text.secondary', mt: 0.5 }}>{caption}</Typography>
      </Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          {!resolved ? (
            <Skeleton variant="rounded" height={156} sx={{ borderRadius: '14px', width: '100%' }} />
          ) : (
            <RevenueCard
              themeKey="daily"
              metric={resolved.daily}
              icon={<PaymentsRoundedIcon sx={{ fontSize: 22 }} />}
            />
          )}
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          {!resolved ? (
            <Skeleton variant="rounded" height={156} sx={{ borderRadius: '14px', width: '100%' }} />
          ) : (
            <RevenueCard
              themeKey="mtd"
              metric={resolved.mtd}
              icon={<CalendarMonthRoundedIcon sx={{ fontSize: 22 }} />}
            />
          )}
        </Grid>
        <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
          {!resolved ? (
            <Skeleton variant="rounded" height={156} sx={{ borderRadius: '14px', width: '100%' }} />
          ) : (
            <RevenueCard
              themeKey="ytd"
              metric={resolved.ytd}
              icon={<InsertChartRoundedIcon sx={{ fontSize: 22 }} />}
              yearMode={yearMode}
              onYearModeChange={onYearModeChange}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default RevenueOverview;
